import * as Sentry from '@sentry/react'

/**
 * Initialise Sentry for the tenant web app.
 *
 * Reads the following Vite env vars at build time:
 *   VITE_SENTRY_DSN              — required to enable
 *   VITE_SENTRY_ENVIRONMENT      — defaults to import.meta.env.MODE
 *   VITE_SENTRY_RELEASE          — defaults to undefined (set in CI)
 *   VITE_SENTRY_TRACES_SAMPLE_RATE — float 0..1, defaults 0.1
 *
 * If VITE_SENTRY_DSN is empty/missing the call is a no-op so local
 * developers don't need to provision a Sentry project just to run the app.
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (!dsn) return

  const tracesRate = parseFloat(
    (import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE as string | undefined) ?? '0.1',
  )

  // Session Replay sample rates — overridable per environment.
  // Default in dev: capture 100% of sessions so you can debug locally without
  // refreshing for ages. In production, drop to 10% to keep ingest cost down.
  const env = (import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined) ?? import.meta.env.MODE
  const isProd = env === 'production'
  const replayRate = parseFloat(
    (import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE as string | undefined) ??
      (isProd ? '0.1' : '1.0'),
  )
  const replayOnErrorRate = parseFloat(
    (import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE as string | undefined) ?? '1.0',
  )

  Sentry.init({
    dsn,
    environment: env,
    release: import.meta.env.VITE_SENTRY_RELEASE as string | undefined,
    tracesSampleRate: Number.isFinite(tracesRate) ? tracesRate : 0.1,
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
      // Capture console.error and console.warn as breadcrumbs + events
      Sentry.captureConsoleIntegration({ levels: ['error', 'warn'] }),
      // Don't mask text/media so the replay actually shows what happened.
      // Tighten these in production if you handle sensitive data.
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    replaysSessionSampleRate: Number.isFinite(replayRate) ? replayRate : (isProd ? 0.1 : 1.0),
    replaysOnErrorSampleRate: Number.isFinite(replayOnErrorRate) ? replayOnErrorRate : 1.0,
  })
}
